import { Autocomplete, CircularProgress, InputAdornment, InputLabel } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import debounce from 'lodash/debounce';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
// Import links for regions
import { v4 as uuidv4 } from 'uuid';

import IconPin from '../../../components/Icons/IconPin';
import IconSearch from '../../../components/Icons/IconSearch';
import Geocoder, { CURRENT_LOCATION_ID } from '../../../components/LocationAutocompleteInput/GeocoderMapbox';
import links from '../../../components/LocationAutocompleteInput/links.json';
import { specialLocations } from '../../../components/LocationAutocompleteInput/specialLocations';
import config from '../../../config';
import defaultLocationSearches from '../../../default-location-searches';
import routeConfiguration from '../../../routeConfiguration';
import { logError } from '../../../util/log';
import { FormattedMessage } from '../../../util/reactIntl';
import { findRouteByRouteName } from '../../../util/routes';

// Import regions for predictions
const regions = {
  de: () => import('../../../translations/de_regions.json'),
  en: () => import('../../../translations/en_regions.json'),
  es: () => import('../../../translations/es_regions.json'),
  fr: () => import('../../../translations/fr_regions.json'),
  it: () => import('../../../translations/it_regions.json'),
  no: () => import('../../../translations/no_regions.json'),
  sv: () => import('../../../translations/sv_regions.json'),
};

// Default predictions
const defaultPredictions = (locale, inEditForm) => {
  const result = config.maps.search.suggestCurrentLocation
    ? [
      {
        id: CURRENT_LOCATION_ID,
        predictionPlace: {},
      },
    ]
    : [];
  return inEditForm ? result : result.concat(defaultLocationSearches(locale));
};

const DEBOUNCE_WAIT_TIME = 500;
const DEBOUNCE_WAIT_TIME_FOR_SHORT_QUERIES = 1000;

// Branded "search" styling, restoring the look of the old LocationAutocompleteInput
// used on the hero, search page and topbar: a seamless borderless input that blends
// into the surrounding search bar, plus a marketplace-coloured predictions dropdown
// with light text and a left-border hover accent. Opt-in via `searchAppearance` so
// regular form fields (edit-listing, route planner) keep the default outlined look.
const SEARCH_DROPDOWN_SX = {
  mt: 1,
  backgroundColor: 'var(--marketplaceColor)',
  borderRadius: 'var(--borderRadius)',
  boxShadow: 'var(--boxShadowPopup)',
  overflow: 'hidden',
  '& .MuiAutocomplete-listbox': {
    padding: '14px 0',
  },
  '& .MuiAutocomplete-option': {
    color: 'var(--matterColorLightTransparent)',
    lineHeight: '24px',
    padding: '10px 24px',
    transition: 'var(--transitionStyleButton)',
  },
  '& .MuiAutocomplete-option:hover, & .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option[aria-selected="true"].Mui-focused':
    {
      backgroundColor: 'transparent',
      color: 'var(--matterColorLight)',
      borderLeft: '6px solid var(--matterColorLight)',
      borderTopRightRadius: '20px',
      borderBottomRightRadius: '20px',
    },
};

// Make the field occupy the FULL height of the surrounding search bar and centre
// its contents, instead of a short input pinned to the top of the pill. The input
// is also made transparent so the search bar's own hover/focus background shows
// through uniformly (otherwise a white input box appears over the grey hover).
const SEARCH_INPUT_SX = {
  // The wrapper (.MuiInputBase-root) fills the full height set by the surrounding
  // search bar and centres the text (its display:flex already aligns center), so
  // the field spans the whole pill instead of a short box pinned to the top.
  '& .MuiInputBase-root': {
    height: '100%',
    backgroundColor: 'transparent',
  },
  '& .MuiInputBase-input': {
    padding: '6px 0',
    backgroundColor: 'transparent',
  },
};

const FieldComponent = ({
  field,
  fieldState,
  label,
  required,
  inputLabelProps,
  fullWidth,
  geocoder,
  intl,
  id,
  loading,
  predictions,
  setPredictions,
  open,
  setOpen,
  handlePlaceSelection,
  fetchPredictions,
  textFieldProps,
  error,
  helperText,
  placeholder,
  clearOnSelect,
  inSearchPage,
  showSubmitIcon,
  onSubmitIcon,
  closeOnBlur,
  preloadHandlers,
  searchAppearance,
  dropdownSx,
}) => {
  const [inputValue, setInputValue] = useState(field.value || '');

  useEffect(() => {
    if (field.value !== undefined) {
      setInputValue(field.value);
    }
  }, [field.value]);

  const handleSelection = (newValue) => {
    if (newValue) {
      handlePlaceSelection(newValue, field.onChange, () => {
        // Wipe the visible input text synchronously after the place has been
        // committed so the typed search query doesn't linger in the box.
        setInputValue('');
        field.onChange('');
      });
    } else {
      field.onChange(inputValue);
    }
  };

  // Restores the old LocationAutocompleteInputImpl in-field magnifier. When
  // `inSearchPage` is set the old impl rendered a static <IconSearch/>; when an
  // in-field submit icon is requested it rendered a clickable icon wired to
  // submit (iconClick / iconClickHandle). Loading state takes precedence and
  // shows the spinner in both cases.
  const startAdornment =
    !loading && (inSearchPage || showSubmitIcon) ? (
      <InputAdornment position="start">
        {showSubmitIcon ? (
          <IconButton aria-label="Submit search" edge="start" onClick={onSubmitIcon} size="small">
            <IconSearch />
          </IconButton>
        ) : (
          <IconSearch />
        )}
      </InputAdornment>
    ) : null;

  return (
    <Stack spacing={1} width="100%" {...preloadHandlers}>
      {label ? (
        <InputLabel htmlFor={id} required={required} shrink={false} {...inputLabelProps}>
          {label}
        </InputLabel>
      ) : null}
      <Autocomplete
        // Parity with the old LocationAutocompleteInput, which was a plain text
        // field with a custom predictions dropdown: no MUI clear (×) button and
        // no popup-toggle (▼) chevron — see disableClearable / forcePopupIcon below.
        disableClearable
        // When closeOnBlur is false (mobile), keep the dropdown open on blur so
        // tapping a prediction doesn't dismiss the list before the tap lands.
        disableCloseOnSelect={false}
        filterOptions={(x) => x} // we handle filtering in fetchPredictions
        forcePopupIcon={false}
        fullWidth={fullWidth}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return option.id === CURRENT_LOCATION_ID
            ? intl.formatMessage({ id: 'LocationAutocompleteInput.currentLocation' })
            : geocoder.getPredictionAddress(option);
        }}
        id={id}
        inputValue={inputValue}
        loading={loading}
        onChange={(event, newValue) => {
          if (clearOnSelect) {
            handleSelection(newValue);
          } else if (newValue) {
            handlePlaceSelection(newValue, field.onChange);
          } else {
            field.onChange(inputValue);
          }
        }}
        onClose={(event, reason) => {
          // Mobile parity: the old impl used closeOnBlur={!isMobile}. MUI closes
          // the popup on blur by default; when closeOnBlur is false we keep it
          // open on blur (but still honor explicit selection/escape/toggle).
          if (closeOnBlur === false && reason === 'blur') {
            return;
          }
          setOpen(false);
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason !== 'reset') {
            setInputValue(newInputValue || '');
            if (newInputValue && newInputValue.length < 3) {
              // For short queries, use longer debounce time
              setTimeout(() => {
                fetchPredictions(newInputValue, setPredictions);
              }, DEBOUNCE_WAIT_TIME_FOR_SHORT_QUERIES - DEBOUNCE_WAIT_TIME);
            } else if (newInputValue) {
              fetchPredictions(newInputValue, setPredictions);
            }
          }
        }}
        onOpen={() => setOpen(true)}
        open={open}
        options={predictions}
        renderInput={(params) => (
          <TextField
            {...params}
            {...textFieldProps}
            InputProps={{
              ...params.InputProps,
              // In search appearance the input is borderless so it blends into the
              // surrounding search bar (the old LocationAutocompleteInput had no box).
              ...(searchAppearance ? { disableUnderline: true } : {}),
              startAdornment: startAdornment || params?.InputProps?.startAdornment,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params?.InputProps?.endAdornment ? params.InputProps.endAdornment : null}
                </>
              ),
            }}
            error={error || fieldState?.invalid}
            helperText={helperText || fieldState?.error?.message}
            placeholder={placeholder}
            required={required}
            size="small"
            sx={searchAppearance ? SEARCH_INPUT_SX : undefined}
            variant={searchAppearance ? 'standard' : 'outlined'}
            {...(textFieldProps?.variant ? { variant: textFieldProps.variant } : {})}
          />
        )}
        renderOption={(props, option) => {
          const key = option.id || `option-${props['data-option-index']}`;
          return (
            <li {...props} key={key}>
              {option.id === CURRENT_LOCATION_ID ? (
                <Box alignItems="center" display="flex">
                  <IconPin sx={{ mr: 1 }} />
                  <FormattedMessage id="LocationAutocompleteInput.currentLocation" />
                </Box>
              ) : (
                geocoder.getPredictionAddress(option)
              )}
            </li>
          );
        }}
        slotProps={
          searchAppearance || dropdownSx
            ? {
                paper: {
                  // Array form so a caller's dropdownSx layers on top of (or fully
                  // replaces) the branded dropdown instead of being swallowed by it.
                  sx: [searchAppearance ? SEARCH_DROPDOWN_SX : null, dropdownSx].filter(Boolean),
                },
              }
            : undefined
        }
        value={field.value}
      />
    </Stack>
  );
};

const ControlledLocationAutocomplete = ({
  name,
  control,
  label = '',
  required = false,
  placeholder = '',
  error = false,
  helperText = '',
  fullWidth = false,
  controllerOptions = {},
  inputLabelProps = {},
  textFieldProps = {},
  useDefaultPredictions = true,
  inEditForm = false,
  onLocationChange,
  clearOnSelect = false,
  // Restored parity props (see LocationAutocompleteInputImpl):
  navigate,
  inSearchPage = false,
  showSubmitIcon = false,
  onSubmitIcon,
  closeOnBlur = true,
  preloadPage = null,
  // Opt-in branded search styling (borderless input + marketplace-coloured
  // dropdown) used by the hero / search page / topbar search forms.
  searchAppearance = false,
  // Styles layered onto the predictions dropdown. Lets a caller keep the
  // borderless search input but drop the marketplace-coloured dropdown.
  dropdownSx = null,
}) => {
  const intl = useIntl();

  if (!name) {
    throw new Error('ControlledLocationAutocomplete: name is required');
  }
  if (!(controllerOptions?.control || control)) {
    throw new Error('ControlledLocationAutocomplete: control is required');
  }

  const geocoder = useMemo(() => new Geocoder(), []);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const id = `id_${name}`;
  inputLabelProps.htmlFor = id;

  textFieldProps.id = id;

  // Restore the old <Form enforcePagePreloadFor="SearchPage"> affordance:
  // hover/touch preloads the loadable page so navigation feels instant.
  const preloadHandlers = useMemo(() => {
    if (!preloadPage) return undefined;
    const onPreload = () => {
      const route = findRouteByRouteName(preloadPage, routeConfiguration());
      if (route?.component?.preload) {
        route.component.preload();
      }
    };
    return { onMouseOver: onPreload, onTouchStart: onPreload };
  }, [preloadPage]);

  // Debounced search function for fetching predictions
  const fetchPredictions = useCallback(
    debounce(async (search, callback) => {
      if (!search) {
        callback([]);
        return;
      }

      setLoading(true);
      const locale = intl.locale;
      const predictionCallBack = inEditForm
        ? 'getPlacePredictionsForAddress'
        : 'getPlacePredictionsForSearch';

      try {
        let regionPredictions = [];

        // Only fetch region predictions if not in edit form
        if (!inEditForm) {
          try {
            // Check for region matches
            const regionNames = await regions[locale]();

            const foundRegion = Object.keys(regionNames)
              .filter((regionName) => regionName.toLowerCase().includes(search.toLowerCase()))
              .map((regionName) => {
                const key = regionNames[regionName];
                // Ensure we have the link data
                const linkData = links[key]
                  ? Object.fromEntries(new URLSearchParams(links[key]))
                  : {};

                return {
                  name: regionName,
                  key,
                  ...linkData,
                };
              });

            if (foundRegion.length > 0) {
              regionPredictions = foundRegion
                .slice(0, 5)
                .filter((region) => region.bounds) // Only process regions with bounds
                .map((region) => {
                  const bounds = region.bounds.split(',');
                  const sw = new window.mapboxgl.LngLat(bounds[0], bounds[1]);
                  const ne = new window.mapboxgl.LngLat(bounds[2], bounds[3]);
                  const boundsObj = new window.mapboxgl.LngLatBounds(sw, ne);
                  return {
                    id: `custom.region_${uuidv4().split('-')[0]}`,
                    type: 'Feature',
                    place_type: ['region'],
                    relevance: 1,
                    text_de: region.name,
                    language_de: locale,
                    place_name_de: region.name,
                    text: region.name,
                    language: locale,
                    place_name: region.name,
                    bbox: bounds.reverse(),
                    center: [boundsObj.getCenter().lat, boundsObj.getCenter().lng],
                    geometry: {
                      type: 'Point',
                      coordinates: [boundsObj.getCenter().lat, boundsObj.getCenter().lng],
                    },
                  };
                });
            }
          } catch (regionError) {
            logError(regionError, 'fetch-predictions-regions');
          }
        }

        // Get predictions from geocoder as well
        const geocoderResults = await geocoder[predictionCallBack](search, locale);

        // Combine region predictions with geocoder predictions
        const allPredictions = [...regionPredictions, ...geocoderResults.predictions].slice(0, 5);

        callback(allPredictions);
      } catch (error) {
        logError(error, 'fetch-predictions');
        callback([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_WAIT_TIME),
    [geocoder, inEditForm, intl.locale],
  );

  // Handle selection of a place
  const handlePlaceSelection = useCallback(
    async (prediction, onChange, afterSelect) => {
      if (!prediction) return;

      try {
        setLoading(true);
        const place = await geocoder.getPlaceDetails(prediction);

        if (prediction.id === CURRENT_LOCATION_ID) {
          place.address = intl.formatMessage({
            id: 'LocationAutocompleteInput.currentLocation',
          });
        }

        // Curated "special location" redirect (parity with the old impl,
        // LocationAutocompleteInputImpl ~lines 330-334): selecting a curated
        // prediction (e.g. Croatia -> the Storyblok wohnmobilstellplatz route)
        // navigates to the curated route and must NOT fire the normal
        // onChange/onLocationChange/submit path.
        if (navigate && specialLocations[intl.locale]?.[prediction.id]) {
          navigate(specialLocations[intl.locale][prediction.id]);
          return;
        }

        onChange(place.address);
        // `context` is absent on Mapbox results like countries or our custom
        // region predictions. Guard with optional chaining so a missing or
        // non-place-context entry doesn't throw and wipe the selection.
        const locationPlace =
          prediction.context?.find?.((item) => item.id?.includes('place'))?.text ?? null;
        // `predictions: []` mirrors the old onChange payload shape, where the
        // final selection always cleared predictions to an empty array.
        onLocationChange({
          search: place.address,
          predictions: [],
          selectedPlace: place,
          locationPlace,
        });
        if (typeof afterSelect === 'function') afterSelect();
      } catch (error) {
        const isGeolocationError =
          typeof GeolocationPositionError !== 'undefined' &&
          error instanceof GeolocationPositionError;
        if (!isGeolocationError) {
          logError(error, 'handle-place-selection');
        }
        onChange('');
        onLocationChange({
          search: '',
          predictions: [],
          selectedPlace: null,
          locationPlace: null,
        });
      } finally {
        setLoading(false);
      }
    },
    [geocoder, intl, navigate, onLocationChange],
  );

  useEffect(() => {
    // Show default predictions when input is empty and dropdown is open
    if (open && useDefaultPredictions) {
      setPredictions(defaultPredictions(intl.locale, inEditForm));
    }
  }, [open, useDefaultPredictions, intl.locale, inEditForm]);

  return (
    <Controller
      control={control}
      name={name}
      {...controllerOptions}
      render={({ field, fieldState }) => (
        <FieldComponent
          clearOnSelect={clearOnSelect}
          closeOnBlur={closeOnBlur}
          dropdownSx={dropdownSx}
          error={error}
          fetchPredictions={fetchPredictions}
          field={field}
          fieldState={fieldState}
          fullWidth={fullWidth}
          geocoder={geocoder}
          handlePlaceSelection={handlePlaceSelection}
          helperText={helperText}
          id={id}
          inSearchPage={inSearchPage}
          inputLabelProps={inputLabelProps}
          intl={intl}
          label={label}
          loading={loading}
          onSubmitIcon={onSubmitIcon}
          open={open}
          placeholder={placeholder}
          predictions={predictions}
          preloadHandlers={preloadHandlers}
          required={required}
          searchAppearance={searchAppearance}
          setOpen={setOpen}
          setPredictions={setPredictions}
          showSubmitIcon={showSubmitIcon}
          textFieldProps={textFieldProps}
        />
      )}
    />
  );
};

ControlledLocationAutocomplete.displayName = 'ControlledLocationAutocomplete';

export default memo(ControlledLocationAutocomplete);
